// Client-side score helpers. Mirrors the server-side `scoreLabel` thresholds
// at .klemento-api/src/lib/prompt/weights.ts so the UI renders the same
// vocabulary the prompt uses (Strong / Solid / Developing / Needs attention
// / Priority focus).
//
// The kit's pill colour variants are 3-band: sage (positive), default ink
// (mid), warning amber (concern). We map the 5 server bands onto those:
//   Strong, Solid       -> sage  (.pill.s)
//   Developing          -> default
//   Needs attention,
//   Priority focus      -> warning  (.pill.w)

export type ScoreBand = "strong" | "solid" | "developing" | "needs-attention" | "priority";

const BAND_LABEL: Record<ScoreBand, string> = {
  strong: "Strong",
  solid: "Solid",
  developing: "Developing",
  "needs-attention": "Needs attention",
  priority: "Priority focus",
};

export function scoreBand(score: number): ScoreBand {
  if (score >= 85) return "strong";
  if (score >= 70) return "solid";
  if (score >= 55) return "developing";
  if (score >= 40) return "needs-attention";
  return "priority";
}

export function scoreLabel(score: number): string {
  return BAND_LABEL[scoreBand(score)];
}

/**
 * Pill colour modifier for the `.pill` class. Empty string for the default
 * (mid) band so callers can compose `pill ${pillVariant(score)}`.
 */
export function pillVariant(score: number): "" | "s" | "w" {
  const band = scoreBand(score);
  if (band === "strong" || band === "solid") return "s";
  if (band === "needs-attention" || band === "priority") return "w";
  return "";
}

/**
 * Same mapping for dimension bar fills. Reuses `.dim-fill.s` and
 * `.dim-fill.w` modifiers in global.css.
 */
export function dimFillVariant(score: number): "" | "s" | "w" {
  return pillVariant(score);
}

/**
 * Compute an overall weighted score from a `scores` map. The server
 * already exposes a per-dimension `weight` so we don't re-derive call-type
 * weighting here.
 */
export function overallScore(
  scores: Record<string, { score: number; weight: number }>,
): number {
  let weighted = 0;
  let totalWeight = 0;
  for (const v of Object.values(scores)) {
    weighted += v.score * v.weight;
    totalWeight += v.weight;
  }
  if (totalWeight === 0) return 0;
  return weighted / totalWeight;
}
